import { X } from 'lucide-react'
import { Button } from '@/components/ds2'
import { formatCount } from '../results/format'
import { FACTORING_BATCH_MIN, FACTORING_BATCH_MIN_SENTENCE } from '../results/refusalWords'

interface FactoringCardProps {
    provider: string
    /** The invoices a batch could take now (`factoringEligibilityOf`): sent, batched and issue-blocked ones are not counted. */
    eligibleCount: number
    /** How many of them are rows of the table above; the rest are invoices created earlier that the table does not list. */
    fromTable?: number
    /** The company's batchable invoices are still being read: the offer says so instead of a count. */
    checking?: boolean
    /** The read failed: the offer says it could not check, and offers to try again, never "nothing is eligible". */
    unchecked?: boolean
    onRetry?: () => void
    onReview: () => void
    onDismiss: () => void
}

/**
 * What the offer says: nothing eligible, too few for a batch, or the batch it offers, and where its invoices are, so a count larger than
 * the table never reads as a mismatch (the rest are invoices created earlier, not in a batch yet).
 */
const offerOf = (eligibleCount: number, fromTable: number): string => {
    if (eligibleCount === 0) return 'No invoice is eligible for a factoring batch right now.'
    if (eligibleCount < FACTORING_BATCH_MIN) return FACTORING_BATCH_MIN_SENTENCE
    const count = formatCount(eligibleCount)
    if (fromTable >= eligibleCount) return `${count} invoices from this list can go into a factoring batch.`
    if (fromTable === 0) return `${count} invoices you created earlier are not in a factoring batch yet. None of the loads above can go into one yet.`
    return `${count} invoices can go into a factoring batch: ${formatCount(fromTable)} from this list and ${formatCount(eligibleCount - fromTable)} created earlier.`
}

/**
 * The chat's factoring offer (v17 flow 15), shown when the user asked about factoring or the table holds an invoice that is created and
 * not sent. Its one action is disabled while fewer invoices are eligible than a batch takes, so it never leads to a batch the prepare
 * card refuses; the × only hides it.
 */
export const FactoringCard = ({
    provider,
    eligibleCount,
    fromTable = eligibleCount,
    checking = false,
    unchecked = false,
    onRetry,
    onReview,
    onDismiss,
}: FactoringCardProps) => (
    <section aria-label="Factoring batch" className="my-3 flex items-start gap-3 rounded-xl bg-accent-soft p-3">
        <div className="flex min-w-0 flex-1 flex-col gap-2">
            <div className="flex flex-col gap-0.5">
                <strong className="text-sm font-semibold text-foreground">Factoring · {provider}</strong>
                <p className="text-xs text-foreground-muted">
                    {checking
                        ? 'Checking which invoices can go into a factoring batch…'
                        : unchecked
                          ? 'Could not check which invoices can go into a factoring batch.'
                          : offerOf(eligibleCount, fromTable)}
                </p>
            </div>
            <div>
                {unchecked && onRetry ? (
                    <Button size="sm" variant="secondary" onClick={onRetry}>
                        Try again
                    </Button>
                ) : (
                    <Button size="sm" onClick={onReview} disabled={checking || eligibleCount < FACTORING_BATCH_MIN}>
                        Review factoring batch
                    </Button>
                )}
            </div>
        </div>
        <button
            type="button"
            aria-label="Dismiss factoring offer"
            onClick={onDismiss}
            className="rounded-full p-1 text-foreground-muted transition-colors duration-150 hover:bg-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
            <X className="h-4 w-4" aria-hidden />
        </button>
    </section>
)
